import { useState } from "react";
import { useRouter } from "next/router";
import { skipToken } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { WorkSiteRole } from "@remi/enums/work-site-role";
import { formatPhoneNumberForDisplay, PhoneNumberFormat } from "@remi/formatting/phone";
import { RnX } from "@remi/icons/next";
import {
  RemiButton,
  RemiCommand,
  RemiCommandEmpty,
  RemiCommandGroup,
  RemiCommandInput,
  RemiCommandItem,
  RemiCommandList,
  RemiInput,
  RemiSelect,
} from "@remi/next-ui";
import type { LiveCallSuggestedAction } from "@remi/types/socket/message";

import { api } from "~/utils/api";
import { useSuggestedAction } from "~/contexts/SuggestedActionContext";

const ROLE_OPTIONS = [
  WorkSiteRole.SPOUSE,
  WorkSiteRole.FAMILY_MEMBER,
  WorkSiteRole.TENANT,
  WorkSiteRole.RESIDENT,
  WorkSiteRole.PROPERTY_MANAGER,
  WorkSiteRole.INSURANCE_AGENT,
  WorkSiteRole.EXTERNAL_CONTRACTOR,
  WorkSiteRole.OTHER,
] as const;

const ROLE_LABELS: Record<(typeof ROLE_OPTIONS)[number], string> = {
  [WorkSiteRole.SPOUSE]: "Spouse",
  [WorkSiteRole.FAMILY_MEMBER]: "Family Member",
  [WorkSiteRole.TENANT]: "Tenant",
  [WorkSiteRole.RESIDENT]: "Resident",
  [WorkSiteRole.PROPERTY_MANAGER]: "Property Manager",
  [WorkSiteRole.INSURANCE_AGENT]: "Insurance Agent",
  [WorkSiteRole.EXTERNAL_CONTRACTOR]: "Contractor",
  [WorkSiteRole.OTHER]: "Other",
};

const ROLE_DATA = ROLE_OPTIONS.map((option) => ({ label: ROLE_LABELS[option], value: option }));

const BID_PICKER_RESULT_LIMIT = 8;
const BID_SEARCH_DEBOUNCE_MS = 400;

const parseRouteBidId = (bidIdParam: string | readonly string[] | undefined): number | null =>
  typeof bidIdParam === "string" && /^\d+$/.test(bidIdParam) ? Number(bidIdParam) : null;

type SuggestedActionMode = "connected" | "goToBid" | "connectForm" | "pickBid" | "none";

export const resolveSuggestedActionMode = ({
  routeBidId,
  matchedBidId,
  pickedBidId,
  hasConnected,
  changingBid,
}: {
  routeBidId: number | null;
  matchedBidId: number | null;
  pickedBidId: number | null;
  hasConnected: boolean;
  changingBid: boolean;
}): SuggestedActionMode => {
  if (hasConnected) {
    return "connected";
  }
  if (changingBid) {
    return "pickBid";
  }
  if (pickedBidId !== null) {
    return "connectForm";
  }
  if (matchedBidId !== null) {
    return matchedBidId === routeBidId ? "none" : "goToBid";
  }
  return routeBidId === null ? "pickBid" : "connectForm";
};

const resolveTargetBidId = ({
  routeBidId,
  pickedBidId,
}: {
  routeBidId: number | null;
  pickedBidId: number | null;
}): number | null => pickedBidId ?? routeBidId;

export const SuggestedActionWidget = () => {
  const { action, dismiss } = useSuggestedAction();
  const router = useRouter();
  if (!action || action.ended) {
    return null;
  }
  const routeBidId = parseRouteBidId(router.query.bidId);
  return <LiveCallCard key={action.phoneCallId} call={action} bidId={routeBidId} onDismiss={dismiss} />;
};

const bidPickerEmptyMessage = (trimmed: string, isFetching: boolean): string => {
  if (trimmed === "") {
    return "Type to search for a bid.";
  }
  return isFetching ? "Searching…" : "No bids found.";
};

interface BidPickerProps {
  onPick: (bidId: number) => void;
  onCancel?: () => void;
}

const BidPicker = ({ onPick, onCancel }: BidPickerProps) => {
  const [search, setSearch] = useState("");
  const [searchValue] = useDebounce(search, BID_SEARCH_DEBOUNCE_MS);
  const trimmed = searchValue.trim();

  const searchResults = api.bid.getSearchResults.useQuery(
    trimmed === ""
      ? skipToken
      : {
          search: trimmed,
          filters: {},
          sort: [],
          includeFilters: false,
          searchIndexOptions: { useFullAddress: true, useHomeownerPhone: true, useBuyerIds: false },
        },
  );

  const bids = searchResults.data?.bids ?? [];
  const visibleBids = bids.slice(0, BID_PICKER_RESULT_LIMIT);

  return (
    <div className="mt-3">
      <RemiCommand shouldFilter={false} label="Search bids" className="max-h-60 shadow-none">
        <RemiCommandInput
          placeholder="Search bids by name, address, or phone"
          value={search}
          onValueChange={setSearch}
          data-testid="live-call-bid-search"
        />
        <RemiCommandList>
          <RemiCommandEmpty className="py-3 text-center text-text-tertiary text-xs">
            {bidPickerEmptyMessage(trimmed, searchResults.isFetching)}
          </RemiCommandEmpty>
          <RemiCommandGroup>
            {visibleBids.map((bid) => (
              <RemiCommandItem
                key={bid.id}
                value={String(bid.id)}
                onSelect={() => onPick(bid.id)}
                data-testid={`live-call-bid-option-${bid.id}`}
              >
                <div className="flex flex-col gap-0.5 py-1.5">
                  <span className="text-text-primary">
                    BID-{bid.id} · {bid.homeownerName || "Unknown homeowner"}
                  </span>
                  <span className="text-text-secondary text-xxs">{bid.address}</span>
                  {bid.phone ? (
                    <span className="text-text-secondary text-xxs">
                      {formatPhoneNumberForDisplay(bid.phone, { format: PhoneNumberFormat.NATIONAL })}
                    </span>
                  ) : null}
                </div>
              </RemiCommandItem>
            ))}
          </RemiCommandGroup>
        </RemiCommandList>
      </RemiCommand>
      {bids.length > BID_PICKER_RESULT_LIMIT ? (
        <p className="mt-1 text-text-tertiary text-xxs">
          Showing first {BID_PICKER_RESULT_LIMIT}. Refine your search to narrow results.
        </p>
      ) : null}
      {onCancel ? (
        <RemiButton variant="tertiary" fullWidth className="mt-1" onClick={onCancel} data-testid="live-call-bid-cancel">
          Cancel
        </RemiButton>
      ) : null}
    </div>
  );
};

interface LiveCallCardProps {
  call: LiveCallSuggestedAction;
  bidId: number | null;
  onDismiss: () => void;
}

const LiveCallCard = ({ call, bidId, onDismiss }: LiveCallCardProps) => {
  const utils = api.useUtils();
  const router = useRouter();
  const [pickedBidId, setPickedBidId] = useState<number | null>(null);
  const [changingBid, setChangingBid] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<(typeof ROLE_OPTIONS)[number]>(WorkSiteRole.OTHER);

  const connect = api.bid.connectContactToBid.useMutation({
    onSettled: (_data, _error, variables) => {
      void utils.bid.getBid.invalidate(variables.bidId);
      void utils.communication.getConversationsByBidId.invalidate({ bidId: variables.bidId });
    },
  });

  const matchedBidId = call.matchedBidId;
  const targetBidId = resolveTargetBidId({ routeBidId: bidId, pickedBidId });
  const mode = resolveSuggestedActionMode({
    routeBidId: bidId,
    matchedBidId,
    pickedBidId,
    hasConnected: connect.isSuccess,
    changingBid,
  });

  return (
    <div
      role="region"
      aria-label="Live call"
      className="fixed bottom-4 left-4 z-50 w-80 rounded-lg border border-border-primary bg-background-primary p-4 shadow-lg"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-text-primary">Live call</p>
          <p className="truncate text-sm text-text-primary">{call.callerName ?? "Unknown caller"}</p>
          <p className="text-text-secondary text-xs">
            {formatPhoneNumberForDisplay(call.callerNumber, { format: PhoneNumberFormat.NATIONAL })}
          </p>
        </div>
        <RemiButton
          variant="tertiary"
          onClick={onDismiss}
          aria-label="Dismiss live call"
          icon={<RnX size="sm" />}
          data-testid="live-call-dismiss"
        />
      </div>

      {mode === "goToBid" && matchedBidId !== null ? (
        <div className="mt-3 space-y-2">
          <RemiButton
            variant="secondary"
            fullWidth
            onClick={() => router.push(`/dashboard/bids/${matchedBidId}`)}
            data-testid="live-call-go-to-bid"
          >
            Go to BID-{matchedBidId}
          </RemiButton>
          <RemiButton
            variant="tertiary"
            fullWidth
            onClick={() => setChangingBid(true)}
            data-testid="live-call-wrong-bid"
          >
            Not this bid? Connect to another
          </RemiButton>
        </div>
      ) : null}

      {mode === "connected" && connect.data ? (
        <p className="mt-3 text-sm text-text-primary">
          {connect.data.reusedExistingPerson ? "Linked existing contact" : "Contact created"} ·{" "}
          {connect.data.conversationsLinked} conversation(s) linked to this bid.
        </p>
      ) : null}

      {mode === "none" ? (
        <p className="mt-3 text-text-secondary text-xs">This caller is already linked to this bid.</p>
      ) : null}

      {mode === "pickBid" ? (
        <BidPicker
          onPick={(id) => {
            setPickedBidId(id);
            setChangingBid(false);
          }}
          onCancel={changingBid ? () => setChangingBid(false) : undefined}
        />
      ) : null}

      {mode === "connectForm" && targetBidId !== null ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-text-secondary text-xs">Connecting to BID-{targetBidId}</p>
            <RemiButton
              variant="tertiary"
              onClick={() => setChangingBid(true)}
              aria-label={`Change bid (currently BID-${targetBidId})`}
              data-testid="live-call-change-bid"
            >
              Change
            </RemiButton>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label htmlFor="live-call-first-name" className="font-medium text-text-secondary text-xs">
                First name
              </label>
              <RemiInput
                id="live-call-first-name"
                placeholder=""
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                data-testid="live-call-first-name"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="live-call-last-name" className="font-medium text-text-secondary text-xs">
                Last name
              </label>
              <RemiInput
                id="live-call-last-name"
                placeholder=""
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                data-testid="live-call-last-name"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="live-call-email" className="font-medium text-text-secondary text-xs">
              Email
            </label>
            <RemiInput
              id="live-call-email"
              placeholder=""
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              data-testid="live-call-email"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="live-call-role" className="font-medium text-text-secondary text-xs">
              Relationship
            </label>
            <RemiSelect
              id="live-call-role"
              selected={role}
              data={ROLE_DATA}
              placeholder="Relationship"
              setSelected={(value) => {
                if (value !== null) {
                  setRole(value);
                }
              }}
            />
          </div>
          <RemiButton
            fullWidth
            loading={connect.isPending}
            disabled={connect.isPending}
            onClick={() => {
              connect.mutate({
                bidId: targetBidId,
                phoneNumber: call.callerNumber,
                firstName: firstName || undefined,
                lastName: lastName || undefined,
                email: email || undefined,
                role,
              });
            }}
            data-testid="live-call-connect"
          >
            Connect to this bid
          </RemiButton>
          {connect.isError ? (
            <p role="alert" className="text-text-error text-xs" data-testid="live-call-error">
              Couldn't connect — try again.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
